import React from 'react';
import Helmet from 'react-helmet';
import { func, shape, string, object, array, arrayOf } from 'prop-types';
import { defaultSEO } from './messages/default.js';
import invariant from 'invariant';

const getDefault = brandId => defaultSEO[brandId];

const getTitle = (fullTitle, titles, defaultData, titleDelimiter) => fullTitle || titles.concat(defaultData?.brandName).join(titleDelimiter);

export const getMetaData = metaData => {
    const { titles = [], brandId, titleDelimiter = ' | ', fullTitle, description, keywords, og, robots, twitter, canonicalUrl } = metaData;
    invariant(brandId, 'brandId is a required param of getMetaData()');
    const defaultData = getDefault(brandId);

    return {
        title: getTitle(fullTitle, titles, defaultData, titleDelimiter),
        description: description,
        keywords: keywords,
        og,
        twitter,
        robots,
        canonicalUrl,
        defaultData,
        titleDelimiter
    };
};

const MetaTags = props => {
    const { metaData, script, toPreconnect, schemaMetaData } = props;
    invariant(metaData, 'metaData is a required param of MetaTags()');
    const meta = getMetaData(metaData);
    const toPreload = script ? [...(metaData.toPreload || []), { href: script, as: 'script' }] : metaData.toPreload;
    return (
        <Helmet>
            <title>{meta.title}</title>
            {meta.description && <meta name="description" content={meta.description} />}
            {meta.keywords && <meta name="keywords" content={meta.keywords} />}
            {getOg(metaData.og, metaData.titles, meta.defaultData, meta.titleDelimiter)}
            {getTwitter(metaData.twitter)}
            {metaData.robots && <meta name="robots" content={metaData.robots} />}
            <link rel="canonical" href={metaData.canonicalUrl} />
            {getPreloadTags(toPreload)}
            {getPreconnectTags(toPreconnect)}
            {script && <script defer src={script} />}
            {schemaMetaData && <script type="application/ld+json">{JSON.stringify(schemaMetaData)}</script>}
        </Helmet>
    );
};

const getOg = (og = {}, titles, defaultData, titleDelimiter) => {
    return Object.entries(og).map(ogTag => {
        let content = ogTag[1];
        if (ogTag[0] === 'title') {
            content = ogTag[1] ? ogTag[1] : getTitle(null, titles, defaultData, titleDelimiter);
        }
        return <meta property={`og:${ogTag[0]}`} content={content} key={ogTag[0]} />;
    });
};

const getTwitter = (twitter = {}) => {
    return Object.entries(twitter).map(twitterTag => <meta name={`twitter:${twitterTag[0]}`} content={twitterTag[1]} key={twitterTag[0]} />);
};

export const getPreloadTags = (toPreload = []) => {
    if (!toPreload.length) return null;
    return toPreload.map(({ href, media, as, imagesrcset, imagesizes, fetchpriority }) => (
        <link
            rel="preload"
            {...(media && { media })}
            {...(href && { href })}
            as={as}
            key={href || imagesrcset}
            {...(imagesrcset && { imagesrcset })}
            {...(imagesizes && { imagesizes })}
            {...(fetchpriority && { fetchpriority })}
        />
    ));
};

export const getPreconnectTags = (toPreconnect = []) => {
    if (!toPreconnect.length) return null;
    return toPreconnect.map(href => <link rel="preconnect" href={href} key={href} />);
};

MetaTags.propTypes = {
    schemaMetaData: object,
    metaData: shape({
        titles: array.isRequired,
        titleDelimiter: string,
        fullTitle: string,
        description: string.isRequired,
        keywords: string.isRequired,
        og: object,
        twitter: object,
        fallbacks: shape({
            description: func,
            keywords: func
        }).isRequired,
        canonicalUrl: string.isRequired,
        toPreload: arrayOf(
            shape({
                href: string,
                as: string,
                imagesrcset: string
            })
        )
    }),
    script: string,
    toPreconnect: array
};

export default MetaTags;
